import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { SEQUENCE_CALL_STATES, SEQUENCE_END_DETAILS, type CallSequenceView, type SequenceEndDetailCode } from "@gioia365/contracts";
import { Button } from "../../design/components/Button";
import { Icon, type IconName } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { StatusPill } from "../../design/components/StatusPill";
import { messageForError } from "../../i18n/errors";
import { formatNumericDateTime } from "../../i18n/format";
import { useT } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { useMarkAnswered, useRestartSequence, useStopSequence } from "../lead-queries";
import { ANSWERED_HOW, BOX_STATE_WORDS, sequenceLine } from "./sequence-texts";
import "./sequence.css";

// La sezione «Sequenza di chiamate» della scheda Lead (piano M5, Task 12; D8, D16, D17; mock-up `scheda-lead.html`, `m-protocol`
// e `m-seq-line`, con `renderSequenceLine` e `renderProtocol` di `mockup.js`): sotto la testata e sopra la postazione, la riga
// «Sequenza: …» con i comandi, poi le caselle (una per chiamata richiesta dalla regola) raggruppate per tentativo, il dettaglio
// della casella toccata con «Vedi nella Timeline» e la legenda. «Ferma la sequenza» e «Il Lead ha risposto» chiedono nella pagina,
// senza finestre; «Riparti con la sequenza» c'è soltanto quando il server lo ammette. I comandi seguono i permessi della vista
// del server (la modifica del Lead, mai nella «Guarda come…»): senza, la sezione si legge soltanto. Sotto i 720 px le caselle
// scorrono di lato dentro il loro riquadro, con il tentativo di adesso in vista. Un Lead che una sequenza non l'ha mai avuta
// (questione 256) mostra soltanto «Riparti con la sequenza» a chi lo può modificare.

type Box = CallSequenceView["calls"][number];
type Asking = "stop" | "answered" | null;
/** Il comando su cui portare il focus, quando la sezione mostra la sequenza di quella versione. */
type FocusRequest = { action: string; version: number | null };

/** Il segno di una casella accanto al numero, perché il colore non basta (`SLOT_ICON` del mock-up). */
const BOX_ICONS: Readonly<Partial<Record<Box["state"], IconName>>> = {
  now: "clock",
  done: "check",
  answered: "phone",
  not_needed: "minus",
  not_made: "close",
  stopped: "stop",
};

/** Lo spazio lasciato a sinistra del tentativo di adesso quando il riquadro scorre (`renderProtocol` del mock-up). */
const SCROLL_GUTTER = 16;

type Props = {
  tab: TabContext;
  leadId: string;
  view: string | null;
  /** La sequenza della scheda: in corso, oppure l'ultima finita; `null` se il Lead non ne ha mai avuta una. */
  sequence: CallSequenceView | null;
  /** Per un Lead senza sequenza: se chi guarda la può far partire (la regola del server: modifica del Lead, fuori da «Guarda come…»). */
  canStart: boolean;
  timeZone: string;
  /** «Vedi nella Timeline»: porta in vista la telefonata. */
  onShowCall: (callId: string) => void;
};

export function SequenceBlock({ tab, leadId, view, sequence, canStart, timeZone, onShowCall }: Props) {
  const t = useT();
  const titleId = useId();
  const stop = useStopSequence(tab, leadId, view);
  const restart = useRestartSequence(tab, leadId, view);
  const answered = useMarkAnswered(tab, leadId, view);
  const [asking, setAsking] = useState<Asking>(null);
  // Una domanda aperta vale per la sequenza di allora: un'altra sequenza, o la stessa finita, la chiude (durante il disegno, come
  // vuole React per lo stato che dipende da una prop).
  const identity = sequence === null ? null : `${sequence.id}:${sequence.state}`;
  const [askedOn, setAskedOn] = useState(identity);
  if (identity !== askedOn) {
    setAskedOn(identity);
    setAsking(null);
  }
  const [problem, setProblem] = useState<unknown>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
  const focusDone = useRef<FocusRequest | null>(null);
  const busy = stop.isPending || restart.isPending || answered.isPending;
  const version = sequence?.version ?? null;

  // Il focus segue la domanda, come nel mock-up: sulla prima risposta quando si apre, sul comando quando si chiude; dopo un
  // comando, sul comando che resta, appena la scheda mostra la sequenza della risposta (la sua versione).
  useEffect(() => {
    if (focusRequest === null || focusDone.current === focusRequest || focusRequest.version !== version) return;
    focusDone.current = focusRequest;
    actionsRef.current?.querySelector<HTMLElement>(`[data-action="${focusRequest.action}"]`)?.focus();
  }, [focusRequest, version]);

  function ask(next: Asking, focus: string) {
    setProblem(null);
    setAsking(next);
    setFocusRequest({ action: focus, version });
  }

  /** Un comando: la domanda si chiude; dopo, il focus va sul comando che resta (rifiutato, su quello di partenza). */
  async function run(command: () => Promise<{ sequence: CallSequenceView }>, back: string) {
    setProblem(null);
    setAsking(null);
    try {
      const after = await command();
      setFocusRequest({ action: after.sequence.state === "open" ? "stop" : "restart", version: after.sequence.version });
    } catch (error) {
      setProblem(error);
      setFocusRequest({ action: back, version });
    }
  }

  const restartButton = (
    <Button variant="ghost" data-action="restart" disabled={busy} onClick={() => void run(() => restart.mutateAsync({ expectedVersion: version ?? 1 }), "restart")}>
      <Icon name="play" />
      {t("callSequence.restart")}
    </Button>
  );
  const problemLine = problem === null ? null : <Reason tone="warning" role="alert" className="g-sequence__problem">{messageForError(t, problem)}</Reason>;

  if (sequence === null) {
    if (!canStart) return null;
    return (
      <section className="g-sequence" aria-labelledby={titleId}>
        <div className="g-sequence__head">
          <h2 id={titleId} className="g-sequence__title">
            {t("callSequence.title")}
          </h2>
          <div className="g-sequence__actions" ref={actionsRef}>
            {restartButton}
          </div>
        </div>
        {problemLine}
      </section>
    );
  }

  const line = sequenceLine(t, sequence);
  const { permissions } = sequence;
  const open = sequence.state === "open";
  let actions: ReactNode = null;
  if (open && asking === "stop" && permissions.canStop) {
    actions = (
      <>
        <span className="g-sequence__ask">{t("callSequence.stopAsk")}</span>
        <Button variant="primary" data-action="stop-yes" disabled={busy} onClick={() => void run(() => stop.mutateAsync({ expectedVersion: sequence.version }), "stop")}>
          {t("callSequence.stopYes")}
        </Button>
        <Button data-action="stop-no" onClick={() => ask(null, "stop")}>
          {t("callSequence.stopNo")}
        </Button>
      </>
    );
  } else if (open && asking === "answered" && permissions.canMarkAnswered) {
    const mark = (how: SequenceEndDetailCode) => run(() => answered.mutateAsync({ how, expectedVersion: sequence.version }), "answered");
    actions = (
      <>
        <span className="g-sequence__ask">{t("callSequence.answeredAsk")}</span>
        {SEQUENCE_END_DETAILS.map((how, index) => (
          <Button key={how} data-action={index === 0 ? "answered-first" : undefined} disabled={busy} onClick={() => void mark(how)}>
            {t(ANSWERED_HOW[how])}
          </Button>
        ))}
        <Button variant="ghost" onClick={() => ask(null, "answered")}>
          {t("common.cancel")}
        </Button>
      </>
    );
  } else if (open && (permissions.canMarkAnswered || permissions.canStop)) {
    actions = (
      <>
        {permissions.canMarkAnswered ? (
          <Button variant="ghost" data-action="answered" disabled={busy} onClick={() => ask("answered", "answered-first")}>
            <Icon name="check" />
            {t("callSequence.answered")}
          </Button>
        ) : null}
        {permissions.canStop ? (
          <Button variant="ghost" data-action="stop" disabled={busy} onClick={() => ask("stop", "stop-yes")}>
            <Icon name="stop" />
            {t("callSequence.stop")}
          </Button>
        ) : null}
      </>
    );
  } else if (!open && permissions.canRestart) {
    actions = restartButton;
  }

  return (
    <section className="g-sequence" aria-labelledby={titleId}>
      <div className="g-sequence__head">
        <h2 id={titleId} className="g-sequence__title">
          {t("callSequence.title")}
        </h2>
        <p className="g-sequence__line">
          <StatusPill tone={line.tone} icon={line.icon}>
            {line.text}
          </StatusPill>
        </p>
        {actions ? (
          <div className="g-sequence__actions" ref={actionsRef}>
            {actions}
          </div>
        ) : null}
      </div>
      {problemLine}
      {/* Una sequenza nuova è un'altra serie di caselle: niente casella scelta di quella di prima. */}
      <SequenceBoxes key={sequence.id} sequence={sequence} timeZone={timeZone} onShowCall={onShowCall} />
    </section>
  );
}

/** Le caselle raggruppate per tentativo, il dettaglio della casella toccata e la legenda. */
function SequenceBoxes({ sequence, timeZone, onShowCall }: { sequence: CallSequenceView; timeZone: string; onShowCall: (callId: string) => void }) {
  const t = useT();
  const groupsRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const open = sequence.state === "open";
  const attempts = [...new Set(sequence.calls.map((box) => box.attempt))].sort((left, right) => left - right);
  const describe = (box: Box) => {
    const params = { number: box.number, attempt: box.attempt, state: t(BOX_STATE_WORDS[box.state]) };
    return box.at === null ? t("callSequence.box", params) : t("callSequence.boxAt", { ...params, at: formatNumericDateTime(timeZone, box.at) });
  };
  const chosen = sequence.calls.find((box) => box.number === selected) ?? null;

  // Sotto i 720 px il riquadro scorre di lato: il tentativo di adesso (o quello dove la sequenza è finita) resta in vista.
  useEffect(() => {
    const container = groupsRef.current;
    const current = container?.querySelector<HTMLElement>(`[data-attempt="${sequence.currentAttempt}"]`);
    if (!container || !current) return;
    const offset = current.getBoundingClientRect().left - container.getBoundingClientRect().left;
    container.scrollLeft = Math.max(0, container.scrollLeft + offset - SCROLL_GUTTER);
  }, [sequence.currentAttempt, sequence.state]);

  return (
    <>
      <div className="g-sequence__groups" ref={groupsRef}>
        {attempts.map((attempt) => {
          const current = open && attempt === sequence.currentAttempt;
          return (
            <fieldset key={attempt} className="g-sequence__group" data-attempt={attempt} data-current={current ? "true" : undefined}>
              <legend>{current ? t("callSequence.attemptNow", { n: attempt }) : t("callSequence.attempt", { n: attempt })}</legend>
              <div className="g-sequence__boxes">
                {sequence.calls
                  .filter((box) => box.attempt === attempt)
                  .map((box) => {
                    const icon = BOX_ICONS[box.state];
                    return (
                      <button
                        key={box.number}
                        type="button"
                        className="g-sequence__box"
                        data-state={box.state}
                        aria-pressed={selected === box.number}
                        aria-label={describe(box)}
                        onClick={() => setSelected(box.number)}
                      >
                        {box.number}
                        {icon ? <Icon name={icon} /> : null}
                      </button>
                    );
                  })}
              </div>
            </fieldset>
          );
        })}
      </div>
      <div className="g-sequence__detail" role="status">
        {chosen ? (
          <>
            <span>{describe(chosen)}</span>
            {chosen.callId !== null && (chosen.state === "done" || chosen.state === "answered") ? (
              <Button variant="ghost" onClick={() => onShowCall(chosen.callId ?? "")}>
                {t("callSequence.seeInTimeline")}
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
      <ul className="g-sequence__legend" aria-label={t("callSequence.legendLabel")}>
        {SEQUENCE_CALL_STATES.map((state) => {
          const icon = BOX_ICONS[state];
          return (
            <li key={state}>
              <span className="g-sequence__box" data-state={state} aria-hidden="true">
                {icon ? <Icon name={icon} /> : null}
              </span>
              {t(BOX_STATE_WORDS[state])}
            </li>
          );
        })}
      </ul>
    </>
  );
}
